import { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { EnvelopeSimple } from "@phosphor-icons/react";

/**
 * Read-only preview of the weekly digest a user will actually receive on
 * Monday. Renders the same summary the /api/notifications/digest/send email
 * builds, so "why did I get this?" support pings drop off.
 *
 * The "Show mood block" switch lets users toggle the biggest-mood-shifts
 * section on/off locally so they can see exactly what their opted-out
 * digest looks like without having to change their server-side preference.
 *
 * Iter 87 — accepts an optional ``impersonateUserId`` prop so admins can
 * dry-run any user's digest (backed by ``?user_id=`` on the preview
 * endpoint). Non-admins hit a 403 upstream if they try to pass one.
 */
export default function WeeklyRecapPreviewModal({ open, onOpenChange, impersonateUserId = null }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMood, setShowMood] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError("");
    setDigest(null);
    const url = impersonateUserId
      ? `/notifications/digest/preview?user_id=${encodeURIComponent(impersonateUserId)}`
      : "/notifications/digest/preview";
    api.get(url)
      .then((r) => setDigest(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Preview failed"))
      .finally(() => setLoading(false));
  }, [open, impersonateUserId]);

  const rank = digest?.rank_change;
  const rankLabel =
    rank === null || rank === undefined ? "—" :
    rank > 0 ? `▲ +${rank}` :
    rank < 0 ? `▼ ${rank}` : "no change";
  const rankColor = rank > 0 ? "profit" : rank < 0 ? "loss" : "text-zinc-500";

  const moodShifts = digest?.mood_shifts || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="weekly-recap-modal"
        className="bg-zinc-900 border-zinc-800 rounded-sm max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="heading text-2xl tracking-tighter flex items-center gap-2">
            <EnvelopeSimple size={22} className="text-[#00C805]" /> Weekly recap preview
            {impersonateUserId && (
              <span
                data-testid="weekly-recap-impersonate-badge"
                className="mono text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/40 px-1.5 py-0.5 rounded-sm"
              >
                admin · user {impersonateUserId.slice(-6)}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {impersonateUserId
              ? `Dry-running the Monday digest that would ship to user ${impersonateUserId}.`
              : "This is exactly what will land in your inbox every Monday at 9 AM local time."}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            data-testid="weekly-recap-error"
            className="py-10 text-center mono text-xs text-red-400"
          >
            {error}
          </div>
        ) : loading || !digest ? (
          <div className="py-10 text-center mono text-xs text-zinc-500">Building your recap…</div>
        ) : (
          <div className="space-y-3" data-testid="weekly-recap-body">
            <Row label="Week" value={digest.week_start ? `${digest.week_start.slice(0, 10)} → ${digest.week_end?.slice(0, 10)}` : "—"} />
            <Row label="Trades" value={digest.num_trades ?? 0} />
            <Row label="Win rate" value={digest.win_rate !== undefined ? fmtPct(digest.win_rate) : "—"} />
            <Row label="Realized P/L" value={fmtMoney(digest.total_realized_pl || 0, { showSign: true })} valueClassName={(digest.total_realized_pl || 0) >= 0 ? "profit" : "loss"} />
            <Row label="Biggest win" value={digest.biggest_win ? `+${fmtMoney(digest.biggest_win)}` : "—"} valueClassName="profit" />
            <Row label="Biggest loss" value={digest.biggest_loss ? fmtMoney(digest.biggest_loss) : "—"} valueClassName="loss" />
            <Row label="Portfolio value" value={fmtMoney(digest.portfolio_value || 0)} />
            <Row label="New followers" value={digest.new_followers ? `+${digest.new_followers}` : "—"} valueClassName={digest.new_followers ? "profit" : ""} />
            <Row label="Beaten-by-copy alerts" value={digest.beaten_by_copy_alerts || "—"} valueClassName={digest.beaten_by_copy_alerts ? "text-amber-400" : ""} />
            <Row label="Rank change" value={rankLabel} valueClassName={rankColor} />

            {/* Mood shifts preview + local toggle so users can see the with/
                without variants without flipping their server-side setting. */}
            <div className="border border-zinc-800 rounded-sm px-3 py-2.5 mt-4">
              <div className="flex items-center justify-between">
                <div className="pr-3">
                  <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
                    Show mood block
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Preview how your email looks with the "biggest mood shifts" section on or off.
                  </div>
                </div>
                <Switch
                  checked={showMood}
                  onCheckedChange={setShowMood}
                  data-testid="digest-mood-preview-toggle"
                  className="data-[state=checked]:bg-[#00C805]"
                />
              </div>
              {showMood && (
                <div className="mt-3" data-testid="digest-mood-block-preview">
                  <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-2">
                    Biggest mood shifts · bull %
                  </div>
                  {moodShifts.length === 0 ? (
                    <div className="mono text-[11px] text-zinc-600" data-testid="digest-mood-empty">
                      No sentiment swings recorded this week — this section will be skipped in your email.
                    </div>
                  ) : (
                    <ul className="space-y-1.5">
                      {moodShifts.map((s) => {
                        const up = (s.delta_pct || 0) > 0;
                        return (
                          <li
                            key={s.symbol}
                            data-testid={`digest-mood-row-${s.symbol}`}
                            className="flex items-baseline justify-between mono text-xs"
                          >
                            <span className="text-white font-semibold">{s.symbol}</span>
                            <span className="text-zinc-500 text-[11px]">
                              {Math.round(s.start_pct)}% → {Math.round(s.end_pct)}%
                            </span>
                            <span className={up ? "profit" : "loss"}>
                              {up ? "▲" : "▼"} {Math.abs(Math.round(s.delta_pct))}%
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
              {!showMood && (
                <div
                  className="mono text-[11px] text-zinc-600 mt-3"
                  data-testid="digest-mood-hidden-hint"
                >
                  Mood section hidden — this is what your digest looks like with the setting opted out.
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, valueClassName = "" }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-800/50 pb-2 last:border-b-0">
      <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest">{label}</span>
      <span className={`mono text-sm ${valueClassName}`}>{value}</span>
    </div>
  );
}
